import axios from "axios";

// Base API configuration
const API_BASE_URL =
  import.meta.env.VITE_API_URL || "http://localhost:5000/api";

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token with caching
let cachedToken = null;
let tokenTimestamp = 0;
const TOKEN_CACHE_DURATION = 5000; // 5 seconds

apiClient.interceptors.request.use(
  (config) => {
    const now = Date.now();
    if (!cachedToken || now - tokenTimestamp > TOKEN_CACHE_DURATION) {
      cachedToken = localStorage.getItem("token");
      tokenTimestamp = now;
    }
    if (cachedToken) {
      config.headers.Authorization = `Bearer ${cachedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: (credentials) => apiClient.post("/auth/login", credentials),
  getProfile: () => apiClient.get("/auth/profile"),
  createUser: (userData) => apiClient.post("/auth/create-user", userData),
  getUsers: (page = 1, perPage = 10) =>
    apiClient.get(`/auth/users?page=${page}&per_page=${perPage}`),
  toggleUserStatus: (userId) =>
    apiClient.put(`/auth/users/${userId}/toggle-status`),
  resetUserPassword: (userId) =>
    apiClient.put(`/auth/users/${userId}/reset-password`),
};

// Categories API
export const categoriesAPI = {
  getCategories: (params = {}) => apiClient.get("/categories/", { params }),
  createCategory: (categoryData) =>
    apiClient.post("/categories/", categoryData),
  getCategory: (categoryId, includeChildren = false) =>
    apiClient.get(
      `/categories/${categoryId}?include_children=${includeChildren}`
    ),
  updateCategory: (categoryId, categoryData) =>
    apiClient.put(`/categories/${categoryId}`, categoryData),
  deleteCategory: (categoryId) => apiClient.delete(`/categories/${categoryId}`),
  getCategoryTree: () => apiClient.get("/categories/tree"),
};

// Uploads API
export const uploadsAPI = {
  uploadFile: (formData, onUploadProgress) =>
    apiClient.post("/uploads/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    }),
  getUploads: (page = 1, perPage = 10, status = null) => {
    const params = { page, per_page: perPage };
    if (status) params.status = status;
    return apiClient.get("/uploads/", { params });
  },
  getUpload: (uploadId) => apiClient.get(`/uploads/${uploadId}`),
  getUploadStatus: (uploadId) => apiClient.get(`/uploads/${uploadId}/status`),
  cancelUpload: (uploadId) => apiClient.put(`/uploads/${uploadId}/cancel`),
  retryUpload: (uploadId) => apiClient.put(`/uploads/${uploadId}/retry`),
  commitUpload: (uploadId) => apiClient.post(`/uploads/${uploadId}/commit`),

  // New endpoints for enhanced upload features
  previewTableName: (filename) =>
    apiClient.post("/uploads/preview-table-name", { filename }),
  checkDuplicate: (filename, databaseId) =>
    apiClient.post("/uploads/check-duplicate", {
      filename,
      database_id: databaseId,
    }),

  // Delete endpoints
  deleteUpload: (uploadId) => apiClient.delete(`/uploads/${uploadId}`),
  clearAllUploads: () => apiClient.delete("/uploads/clear-all"),
};

// Database API
export const databaseAPI = {
  // List all databases with optional table inclusion
  listDatabases: (params = {}) =>
    apiClient.get("/database/databases", {
      params: {
        include_tables: params.includeTables || false,
        ...params,
      },
    }),

  // Get databases for dropdown (simplified format)
  getDatabasesDropdown: () => apiClient.get("/database/dropdown"),

  // Get user-specific databases and tables (for regular users)
  getUserTables: (params = {}) =>
    apiClient.get("/database/user-tables", {
      params: {
        include_tables: params.includeTables || false,
        ...params,
      },
    }),

  // List tables in a specific database
  listTables: (databaseName, params = {}) =>
    apiClient.get(`/database/databases/${databaseName}/tables`, {
      params: {
        include_columns: params.includeColumns || false,
        ...params,
      },
    }),

  // List columns in a specific table
  listColumns: (databaseName, tableName) =>
    apiClient.get(
      `/database/databases/${databaseName}/tables/${tableName}/columns`
    ),

  // Execute dynamic query with filters
  executeQuery: (queryData) => apiClient.post("/database/query", queryData),

  // Export data functionality
  exportData: (exportData) => {
    const token = localStorage.getItem("token");
    return apiClient.post("/database/export", exportData, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },
  getExportStatus: (taskId) => {
    const token = localStorage.getItem("token");
    return apiClient.get(`/database/export/${taskId}/status`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },
  downloadExport: (taskId) => {
    const token = localStorage.getItem("token");
    return apiClient.get(`/database/export/${taskId}/download`, {
      responseType: 'blob',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },

  // Duplicate detection and management
  findDuplicates: (duplicateData) =>
    apiClient.post("/database/duplicates", duplicateData),
  getDuplicateResults: (taskId) =>
    apiClient.get(`/database/duplicates/${taskId}/results`),
  removeDuplicates: (removeData) =>
    apiClient.post("/database/duplicates/remove", removeData),

  // Delete endpoints
  deleteDatabase: (databaseName) =>
    apiClient.delete(`/database/databases/${databaseName}`),
  deleteTable: (databaseName, tableName) =>
    apiClient.delete(`/database/databases/${databaseName}/tables/${tableName}`),

  // Sync and cleanup
  cleanupOrphanedData: () => apiClient.post("/database/sync/cleanup"),

  // Schema management (admin only)
  listSchemas: () => apiClient.get("/database/schemas"),
  getSchemaTables: (schemaNames) => {
    return apiClient.post('/database/schema-tables', { schema_names: schemaNames });
  },

  getSchemaTablesColumns: (tableIdentifiers) => {
    return apiClient.post('/database/schema-tables/columns', { table_identifiers: tableIdentifiers });
  },

  // Legacy methods for backward compatibility
  getDatabases: (includeTables = false) =>
    apiClient.get(`/database/databases`, {
      params: { include_tables: includeTables },
    }),
  getTables: (databaseName, includeColumns = false) =>
    apiClient.get(
      `/database/databases/${databaseName}/tables?include_columns=${includeColumns}`
    ),
  getColumns: (databaseName, tableName) =>
    apiClient.get(
      `/database/databases/${databaseName}/tables/${tableName}/columns`
    ),
};

// Dashboard API
export const dashboardAPI = {
  getOverview: () => apiClient.get("/dashboard/overview"),
  getStatistics: (days = 30, category = "all") =>
    apiClient.get(`/dashboard/statistics?days=${days}&category=${category}`),
  getActivity: (days = 7, limit = 50) =>
    apiClient.get(`/dashboard/activity?days=${days}&limit=${limit}`),
  getHealth: () => apiClient.get("/dashboard/health"),
  refreshStatistics: () => apiClient.post("/dashboard/refresh-statistics"),
};

// Column Mapping API
export const columnMappingAPI = {
  previewMapping: (formData) =>
    apiClient.post("/column-mapping/preview", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  validateMapping: (data) => apiClient.post("/column-mapping/validate", data),
};

// Smart Merge API
export const smartMergeAPI = {
  analyzeColumns: (formData) =>
    apiClient.post("/smart-merge/analyze-columns", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  executeMerge: (formData) =>
    apiClient.post("/smart-merge/execute-merge", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  getMergeStatus: (taskId) => apiClient.get(`/smart-merge/merge-status/${taskId}`),
};

// Task monitoring API
export const taskAPI = {
  getTaskStatus: (taskId, taskType = "file_processing") => {
    // This would typically be implemented with WebSocket for real-time updates
    // For now, we'll use polling
    return apiClient.get(`/tasks/${taskType}/${taskId}/status`);
  },
};

// Generic file upload with progress
export const uploadWithProgress = (url, formData, onProgress) => {
  return apiClient.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress: (progressEvent) => {
      const percentCompleted = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total
      );
      if (onProgress) {
        onProgress(percentCompleted);
      }
    },
  });
};

// Helper function to handle API errors
export const handleAPIError = (error) => {
  if (error.response) {
    // Server responded with error status
    return {
      message: error.response.data?.error || "An error occurred",
      status: error.response.status,
      details: error.response.data,
    };
  } else if (error.request) {
    // Request was made but no response received
    return {
      message: "Network error - please check your connection",
      status: 0,
      details: null,
    };
  } else {
    // Something else happened
    return {
      message: error.message || "An unexpected error occurred",
      status: 0,
      details: null,
    };
  }
};

export default apiClient;
